import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";
import { useEffect, useMemo, useRef, useState } from "react";

import { List, Refresh } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import ClearIcon from "@mui/icons-material/FilterAltOff";
import ClearSortingIcon from "@mui/icons-material/MobiledataOff";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import { lighten } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";

import { createOmniPaginationObject, formatIsoDateString } from "../../utils/helpers";

const formatCellValue = value => {
  if (value == null) {
    return "";
  }

  if (value === "") {
    return "";
  }

  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  if (Array.isArray(value)) {
    return value
      .map(item => formatCellValue(item))
      .filter(Boolean)
      .join(", ");
  }

  if (typeof value === "object") {
    if (Object.keys(value).length === 0) {
      return "";
    }

    if (value.marker != null) {
      return [
        value.marker,
        value.value != null ? formatCellValue(value.value) : null,
        value.weight != null ? `weight: ${value.weight}` : null,
      ]
        .filter(Boolean)
        .join(" - ");
    }

    if (value.label || value.url) {
      return [value.label, value.url].filter(Boolean).join(" - ");
    }

    if (value.thumbnail || value.hero) {
      return [value.thumbnail, value.hero].filter(Boolean).join(", ");
    }

    if (value.vertical || value.campaignPriority != null) {
      return [value.vertical, value.campaignPriority]
        .filter(value => value != null && value !== "")
        .join(", ");
    }

    return Object.values(value)
      .map(item => formatCellValue(item))
      .filter(Boolean)
      .join(", ");
  }

  return value;
};

const getTextCellProps = theme => {
  return {
    sx: {
      whiteSpace: "normal",
      wordWrap: "break-word",
      borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
      padding: "4px 3px",
      verticalAlign: "top",
    },
  };
};

const getCenteredCellProps = theme => {
  return {
    align: "center",
    sx: {
      whiteSpace: "normal",
      wordWrap: "break-word",
      borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
      padding: "4px 3px",
      verticalAlign: "top",
    },
  };
};

const renderTextCell = cell => {
  return (
    <Typography
      variant="body2"
      sx={{
        display: "-webkit-box",
        WebkitLineClamp: 4,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "normal",
        wordBreak: "break-word",
        fontSize: "0.74rem",
        lineHeight: 1.1,
      }}
    >
      {formatCellValue(cell.getValue())}
    </Typography>
  );
};

const renderCenteredTextCell = cell => {
  return (
    <Typography
      variant="subtitle1"
      sx={{
        textAlign: "center",
        fontSize: "0.76rem",
        lineHeight: 1.1,
      }}
    >
      {formatCellValue(cell.getValue())}
    </Typography>
  );
};

const calculateColumns = theme => {
  return [
    {
      accessorKey: "createdAt",
      header: "Date",
      size: 75,
      maxSize: 90,
      enableColumnFilterModes: false,
      enableSorting: true,
      enableColumnFilter: false,
      grow: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          fontSize: "32px",
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
          padding: "4px 3px",
        },
      },
      Cell: ({ cell }) => {
        if (!cell.getValue()) {
          return "";
        }

        const dateString = formatIsoDateString(cell.getValue()).split(",");
        const date = new Date(cell.getValue());
        const options = {
          hour: "numeric",
          minute: "numeric",
          hour12: true,
        };

        return (
          <Box component="span">
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                marginBottom: "2px",
                borderBottom: `2px solid`,
                fontSize: "0.9rem",
              }}
            >
              {dateString[0]}
            </Typography>
            <Typography
              variant="body1"
              fontWeight={100}
              sx={{ textAlign: "center", fontSize: "0.75rem" }}
            >
              {date.toLocaleTimeString("en-US", options)}
            </Typography>
          </Box>
        );
      },
    },
    {
      accessorKey: "type",
      header: "Type",
      size: 80,
      maxSize: 100,
      enableColumnFilterModes: false,
      enableSorting: true,
      grow: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: getCenteredCellProps(theme),
      Cell: ({ cell }) => renderCenteredTextCell(cell),
    },
    {
      accessorKey: "archived",
      header: "Archived",
      size: 75,
      maxSize: 95,
      enableColumnFilterModes: false,
      enableSorting: true,
      grow: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: getCenteredCellProps(theme),
      Cell: ({ cell }) => renderCenteredTextCell(cell),
    },
    {
      accessorKey: "name",
      header: "Name",
      size: 110,
      maxSize: 150,
      enableColumnFilterModes: false,
      enableSorting: true,
      grow: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: getCenteredCellProps(theme),
      Cell: ({ cell }) => renderCenteredTextCell(cell),
    },
    {
      accessorKey: "title",
      header: "Title",
      size: 130,
      maxSize: 180,
      enableColumnFilterModes: false,
      enableSorting: true,
      grow: false,
      muiTableBodyCellProps: getTextCellProps(theme),
      Cell: ({ cell }) => renderTextCell(cell),
    },
    {
      accessorKey: "summary",
      header: "Summary",
      size: 220,
      minSize: 150,
      maxSize: 280,
      enableColumnFilterModes: false,
      enableSorting: true,
      grow: true,
      muiTableBodyCellProps: getTextCellProps(theme),
      Cell: ({ cell }) => renderTextCell(cell),
    },
    {
      accessorKey: "startDate",
      header: "Start Date",
      size: 90,
      maxSize: 110,
      enableColumnFilterModes: false,
      enableSorting: true,
      grow: false,
      muiTableBodyCellProps: getTextCellProps(theme),
      Cell: ({ cell }) => renderTextCell(cell),
    },
    {
      accessorKey: "endDate",
      header: "End Date",
      size: 90,
      maxSize: 110,
      enableColumnFilterModes: false,
      enableSorting: true,
      grow: false,
      muiTableBodyCellProps: getTextCellProps(theme),
      Cell: ({ cell }) => renderTextCell(cell),
    },
    {
      accessorKey: "cta",
      header: "CTA",
      size: 120,
      maxSize: 160,
      enableColumnFilterModes: false,
      enableSorting: false,
      enableColumnFilter: false,
      grow: false,
      muiTableBodyCellProps: getTextCellProps(theme),
      Cell: ({ cell }) => renderTextCell(cell),
    },
    {
      accessorKey: "assets",
      header: "Assets",
      size: 160,
      minSize: 120,
      maxSize: 220,
      enableColumnFilterModes: false,
      enableSorting: false,
      enableColumnFilter: false,
      grow: true,
      muiTableBodyCellProps: getTextCellProps(theme),
      Cell: ({ cell }) => renderTextCell(cell),
    },
    {
      accessorKey: "metadata",
      header: "Metadata",
      size: 140,
      minSize: 120,
      maxSize: 200,
      enableColumnFilterModes: false,
      enableSorting: false,
      enableColumnFilter: false,
      grow: true,
      muiTableBodyCellProps: getTextCellProps(theme),
      Cell: ({ cell }) => renderTextCell(cell),
    },
    {
      accessorKey: "priority",
      header: "Priority",
      size: 90,
      maxSize: 110,
      enableColumnFilterModes: false,
      enableSorting: true,
      grow: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: getCenteredCellProps(theme),
      Cell: ({ cell }) => renderCenteredTextCell(cell),
    },
    {
      accessorKey: "markers",
      header: "Markers",
      size: 180,
      minSize: 140,
      maxSize: 240,
      enableColumnFilterModes: false,
      enableSorting: true,
      grow: true,
      muiTableBodyCellProps: getTextCellProps(theme),
      Cell: ({ cell }) => renderTextCell(cell),
    },
    {
      accessorKey: "updatedAt",
      header: "Updated",
      size: 75,
      maxSize: 90,
      enableColumnFilterModes: false,
      enableSorting: true,
      grow: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: getCenteredCellProps(theme),
      Cell: ({ cell }) => renderCenteredTextCell(cell),
    },
    {
      accessorKey: "id",
      header: "ID",
      size: 75,
      maxSize: 90,
      enableColumnFilterModes: false,
      enableSorting: true,
      grow: false,
      muiTableBodyCellProps: getTextCellProps(theme),
      Cell: ({ cell }) => {
        return (
          <Typography variant="subtitle1" sx={{ fontSize: "0.76rem" }}>
            {formatCellValue(cell.getValue())}
          </Typography>
        );
      },
    },
  ];
};

const getInitialState = () => {
  return {
    density: "compact",
    columnOrder: [
      "mrt-row-select",
      "mrt-row-actions",
      "createdAt",
      "type",
      "archived",
      "name",
      "title",
      "summary",
      "startDate",
      "endDate",
      "cta",
      "assets",
      "metadata",
      "priority",
      "markers",
      "updatedAt",
      "id",
    ],
    columnVisibility: {
      id: false,
      createdAt: true,
      updatedAt: false,
      type: true,
      archived: false,
      name: true,
      title: true,
      summary: true,
      startDate: true,
      endDate: true,
      cta: false,
      assets: false,
      metadata: false,
      priority: true,
      markers: true,
    },
  };
};

export const BotpressProductsTable = props => {
  const {
    data,
    onRequestNewPage = null,
    dataReduxPagination = null,
    totalItemCount = 0,
    onRefreshTable = null,
    onEditEntry = null,
  } = props;

  const theme = useTheme();
  const mobileLayout = useMediaQuery(theme.breakpoints.down("sm"));
  const tableContainerRef = useRef(null);

  const columns = useMemo(() => calculateColumns(theme), [theme]);
  const initialState = useMemo(() => getInitialState(), []);

  const [rowCount, setRowCount] = useState(totalItemCount);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshIconSpinning, setIsRefreshIconSpinning] = useState(false);
  const [rowSelection, setRowSelection] = useState({});

  const [sorting, setSorting] = useState([
    {
      id: "createdAt",
      desc: true,
    },
  ]);

  const [columnFilters, setColumnFilters] = useState([]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });

  const [columnOrder, setColumnOrder] = useState(initialState.columnOrder);

  useEffect(() => {
    setRowCount(totalItemCount);
  }, [totalItemCount]);

  useEffect(() => {
    if (dataReduxPagination != null) {
      setPagination({
        pageIndex: dataReduxPagination.pageIndex,
        pageSize: dataReduxPagination.pageSize,
      });
      setRowCount(dataReduxPagination.totalItems);
    }
  }, [dataReduxPagination]);

  useEffect(() => {
    setIsLoading(false);
  }, [data]);

  useEffect(() => {
    const fetchData = async () => {
      if (!onRequestNewPage) {
        return;
      }

      if (!data.length) {
        setIsLoading(true);
      }

      try {
        let newQuery = {};

        columnFilters.forEach(element => {
          if (typeof element.value === "string") {
            if (element.value === "NULL") {
              newQuery[element.id] = null;
            } else {
              newQuery[element.id] = {
                $regex: `${element.value}`,
              };
            }
          } else if (typeof element.value === "boolean") {
            newQuery[element.id] = element.value;
          } else {
            newQuery[element.id] = element.value;
          }
        });

        let newSorting = {};
        sorting.forEach(element => {
          newSorting[element.id] = element.desc ? "desc" : "asc";
        });

        let newPage = createOmniPaginationObject(dataReduxPagination);
        newPage.pageIndex = pagination.pageIndex;
        newPage.pageSize = pagination.pageSize;
        newPage.query = newQuery;
        newPage.sort = newSorting;

        onRequestNewPage(newPage);
      } catch (error) {
        console.error(error);
        setIsLoading(false);
        return;
      }
    };

    fetchData();
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters, sorting, pagination.pageIndex, pagination.pageSize]);

  const table = useMaterialReactTable({
    columns,
    data,
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableColumnResizing: false,
    enableGrouping: false,
    enableColumnPinning: false,
    enableFacetedValues: true,
    enableRowActions: onEditEntry != null,
    enableRowSelection: true,
    initialState: initialState,
    enableStickyHeader: true,
    enableColumnActions: false,
    positionActionsColumn: "first",
    positionToolbarAlertBanner: "bottom",
    rowCount,
    getRowId: row => row.id,
    onRowSelectionChange: setRowSelection,
    onColumnOrderChange: setColumnOrder,
    defaultColumn: {
      minSize: 60,
      maxSize: 220,
      size: 100,
      grow: false,
    },
    state: {
      columnFilters,
      isLoading,
      pagination,
      sorting,
      columnOrder,
      rowSelection,
    },
    muiSearchTextFieldProps: {
      size: "small",
      variant: "outlined",
    },

    paginationDisplayMode: "pages",

    enableFirstPageButton: false,
    enableLastPageButton: false,

    manualPagination: true,
    onColumnFiltersChange: updaterOrValue => {
      setColumnFilters(prev => {
        const nextValue =
          typeof updaterOrValue === "function" ? updaterOrValue(prev) : updaterOrValue;
        return nextValue;
      });
    },
    onPaginationChange: newPagination => {
      setPagination(newPagination);
    },
    manualSorting: true,
    manualFiltering: true,
    onSortingChange: setSorting,

    muiPaginationProps: {
      color: "primary",
      shape: "rounded",
      showRowsPerPage: false,
      variant: "outlined",
    },

    layoutMode: "grid",
    muiToolbarAlertBannerProps: {
      sx: {
        backgroundColor: "transparent !important",
        boxShadow: "none",
        color: theme.palette.commonground.contrastText,

        "&.MuiAlert-root": {
          backgroundColor: "transparent !important",
          boxShadow: "none",
        },

        "& .MuiAlert-message": {
          color: theme.palette.commonground.contrastText,
        },

        "& .MuiAlert-action": {
          color: theme.palette.secondary.main,
        },

        "& button": {
          color: `${theme.palette.secondary.main} !important`,
        },

        "& .MuiButton-root": {
          color: `${theme.palette.secondary.main} !important`,
        },
      },
    },
    muiTopToolbarProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.secondary.main,
        "& .MuiIconButton-root": {
          marginLeft: theme.spacing(1),
          backgroundColor: theme.palette.commonground.main,
          color: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
        "& .MuiIconButton-root-active": {
          marginLeft: theme.spacing(1),
          color: theme.palette.secondary.contrastText,
          backgroundColor: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
      },
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.contrastText,
        height: "64px",
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        height: "100%",
        overflow: "hidden",
      },
    },
    muiTableContainerProps: {
      ref: tableContainerRef,
      sx: mobileLayout
        ? {
            minHeight: "564px",
            maxHeight: "564px",
            overflowX: "hidden",
            backgroundColor: theme.palette.commonground.main,
            color: theme.palette.commonground.main,
            borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
            borderRight: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
          }
        : {
            minHeight: "424px",
            height: `calc(100% - 120px)`,
            overflowX: "hidden",
            backgroundColor: theme.palette.commonground.main,
            color: theme.palette.commonground.main,
            border: `solid 0.5px ${theme.palette.divider}`,
          },
    },
    muiTableProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.main,
        tableLayout: "fixed",
        width: "100%",
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: lighten(theme.palette.commonground.contrastText, 0.5),
        color: theme.palette.commonground.contrastText,
        justifyContent: "start",
        fontWeight: "bold",
        fontSize: "0.72rem",
        padding: "3px 3px",
        whiteSpace: "nowrap",
      },
    },
    displayColumnDefOptions: {
      "mrt-row-select": {
        size: 25,
        grow: false,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            color: theme.palette.secondary.main,
            padding: "4px 2px",
          },
        },
      },
      "mrt-row-actions": {
        size: 80,
        grow: false,
        header: "Actions",
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            padding: "4px 6px",
            borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
          },
        },
      },
    },
    icons: {
      MoreHorizIcon: () => (
        <List
          color={"secondary"}
          sx={{
            borderRadius: 16,
            width: 34,
            height: 34,
            padding: "8px",
            border: `2px solid ${theme.palette.secondary.main}`,
          }}
        />
      ),
    },
    renderRowActionMenuItems: ({ row, closeMenu }) =>
      [
        onEditEntry != null && (
          <MenuItem
            key="edit"
            onClick={() => {
              onEditEntry(row.original);
              closeMenu();
            }}
          >
            <EditIcon fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
            <ListItemText disableTypography={true}>
              <Typography variant="h5" color="textSecondary">
                Edit Product
              </Typography>
            </ListItemText>
          </MenuItem>
        ),
      ].filter(Boolean),
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        {sorting.length > 0 && (
          <Tooltip title="Clear All Sorting" arrow>
            <IconButton
              className="MuiIconButton-root-active"
              onClick={() => {
                setSorting([]);
              }}
            >
              <ClearSortingIcon />
            </IconButton>
          </Tooltip>
        )}

        {columnFilters.length > 0 && (
          <Tooltip title="Clear All Filters" arrow>
            <IconButton
              className="MuiIconButton-root-active"
              onClick={() => {
                setColumnFilters([]);
              }}
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
        )}

        {onRefreshTable && (
          <Tooltip title="Refresh products" arrow>
            <IconButton
              onClick={() => {
                setIsRefreshIconSpinning(true);
                onRefreshTable();

                setTimeout(() => {
                  setIsRefreshIconSpinning(false);
                }, 800);
              }}
            >
              <Refresh
                sx={{
                  transformOrigin: "center",
                  animation: isRefreshIconSpinning ? "refreshSpin 0.8s linear 1" : "none",
                  "@keyframes refreshSpin": {
                    from: {
                      transform: "rotate(0deg)",
                    },
                    to: {
                      transform: "rotate(360deg)",
                    },
                  },
                }}
              />
            </IconButton>
          </Tooltip>
        )}

        {/* eslint-disable react/jsx-pascal-case */}
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        {/* eslint-enable react/jsx-pascal-case */}
      </Box>
    ),
  });

  return <MaterialReactTable table={table} />;
};

export default BotpressProductsTable;
